import { Stack, Text } from "@chakra-ui/react";
import { ListWithLabel } from "@/components/list";
import { TEXT_VARIANTS } from "@/theme/components/text";

const DisplayList = ({
  text,
  points,
}: {
  text: string;
  points: { label: string; text: string }[];
}) => {
  return (
    <Stack spacing={3}>
      <Text variant={TEXT_VARIANTS.BODY2} color="text.100">
        {text}
      </Text>
      <ListWithLabel points={points} />
    </Stack>
  );
};

export const FAQS = [
  {
    title: "What are the benefits of using a private Kubernetes cluster?",
    content:
      "Private Kubernetes clusters offer enhanced security by isolating cluster components from the public internet, reducing potential attack surfaces. They also provide better compliance alignment by controlling access and data flow within the cluster.",
  },
  {
    title: "How can I access the API server in a private Kubernetes cluster?",
    content:
      "Access to the API server in a private Kubernetes cluster is typically managed through secure methods such as Virtual Private Networks (VPNs) or bastion hosts, ensuring that only authorized personnel can interact with the cluster.",
  },
  {
    title: "What networking solutions are recommended for private Kubernetes clusters?",
    content:
      "Implementing network policies to control traffic between pods and services is essential. Utilizing tools like Weave CNI plugin can enable internal networking, and CoreDNS can serve as the internal DNS for service discovery.",
  },
  {
    title: "How is persistent data managed in private Kubernetes clusters?",
    content:
      "Persistent data in private Kubernetes clusters is managed using Persistent Volumes (PV) and Persistent Volume Claims (PVC). The platform supports automatic configuration of NFS storage provisioners, facilitating dynamic volume provisioning.",
  },
  {
    title: "Can I expose services externally in a private Kubernetes cluster?",
    content:
      "Yes, services can be exposed externally by configuring the appropriate service types such as NodePort or LoadBalancer. However, it's crucial to implement proper security measures to control access.",
  },
  {
    title: "What are the security considerations for private Kubernetes clusters?",
    content:
      "Security considerations include implementing strict network policies, regular auditing, monitoring and logging, and ensuring that access to the API server is tightly controlled through secure methods.",
  },
  {
    title: "How does a private Kubernetes cluster help with compliance requirements?",
    content:
      "Private Kubernetes clusters support compliance standards such as GDPR, HIPAA, SOC 2, and ISO 27001 by ensuring data isolation, access controls, and secure network segmentation.",
  },
  {
    title:
      "What role does RBAC (Role-Based Access Control) play in private Kubernetes clusters?",
    content:
      "RBAC is crucial for managing user permissions in a private Kubernetes cluster. It ensures that only authorized users and services can interact with critical components, reducing security risks.",
  },
  {
    title: "How can I scale a private Kubernetes cluster?",
    content: (
      <DisplayList
        text="Scaling in a private Kubernetes cluster can be manual or automated:"
        points={[
          {
            label: "Horizontal Scaling",
            text: "Kubernetes Horizontal Pod Autoscaler (HPA) adjusts the number of pods based on CPU and memory usage.",
          },
          {
            label: "Vertical Scaling",
            text: "Kubernetes Vertical Pod Autoscaler (VPA) increases or decreases resource allocations for each pod.",
          },
          {
            label: "Cluster Autoscaler",
            text: "Automatically adds or removes nodes based on demand.",
          },
          {
            label: "Hybrid Scaling",
            text: "Integrates Kubernetes with cloud-based load balancers for seamless hybrid expansion.",
          },
        ]}
      />
    ),
  },
  {
    title: "How does Kubernetes handle DNS in private clusters?",
    content:
      "Kubernetes uses CoreDNS for service discovery. It resolves internal service names to private IP addresses so pods can communicate without exposure to external traffic. You can customize DNS resolution by modifying the CoreDNS configmap in your cluster.",
  },
  {
    title: "What tools can I use for monitoring private Kubernetes clusters?",
    content: (
      <DisplayList
        text="Popular Kubernetes monitoring and logging tools include:"
        points={[
          {
            label: "Prometheus + Grafana",
            text: "For real-time monitoring and visualization.",
          },
          {
            label: "Fluentd / ELK Stack",
            text: "Centralized logging for debugging and security analysis.",
          },
          {
            label: "Kubernetes Dashboard",
            text: "Provides insights into resource utilization.",
          },
          {
            label: "Jaeger / OpenTelemetry",
            text: "Distributed tracing for debugging microservices.",
          },
        ]}
      />
    ),
  },
  {
    title: "How do private Kubernetes clusters support high availability?",
    content: (
      <DisplayList
        text="A highly available private Kubernetes cluster ensures zero downtime through:"
        points={[
          {
            label: "Multi-Master Node Configuration",
            text: "Deploying at least three control plane nodes for redundancy.",
          },
          {
            label: "ETCD Database Replication",
            text: "Ensuring state persistence and fault tolerance.",
          },
          {
            label: "Pod Anti-Affinity Policies",
            text: "Preventing pods from running on the same node to distribute workloads.",
          },
          {
            label: "Load Balancers & Ingress Controllers",
            text: "Efficiently managing traffic routing and failover mechanisms.",
          },
        ]}
      />
    ),
  },
];
